import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { GitBranch, CheckCircle, XCircle } from 'lucide-react';

const TreeOfThoughtsSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Exploring multiple solution strategies before committing"
      >
        Tree-of-Thoughts (ToT)
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Description */}
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Application: Network Design</h3>
            
            <div className="slide-card mb-4">
              <p className="text-muted-foreground mb-4">
                <strong className="text-foreground">Problem:</strong> "Design a high-availability network for a data center with 500 servers"
              </p>
              
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm text-foreground font-medium">Approach 1: Traditional 3-tier</p>
                  <p className="text-xs text-muted-foreground">Core-Aggregation-Access with HSRP/VSS</p>
                </div>
                
                <div className="p-3 rounded-lg bg-accent/20 border border-accent/30">
                  <p className="text-sm text-foreground font-medium">Approach 2: Spine-Leaf ✓</p>
                  <p className="text-xs text-muted-foreground">8 spine, 32 leaf, BGP EVPN overlay</p>
                </div>
                
                <div className="p-3 rounded-lg bg-muted/30">
                  <p className="text-sm text-foreground font-medium">Approach 3: Hybrid</p>
                  <p className="text-xs text-muted-foreground">Mix of traditional + fabric</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
              <p className="text-sm text-muted-foreground">
                <strong className="text-accent">Selected:</strong> Spine-Leaf (Approach 2)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Best balance of scalability and resilience for 500-server deployment
              </p>
            </div>
          </div>
          
          {/* Tree visualization */}
          <div className="slide-card">
            <h4 className="font-heading text-lg font-semibold text-foreground mb-6 text-center">Decision Tree</h4>
            
            <div className="flex flex-col items-center">
              {/* Root */}
              <div className="px-4 py-2 rounded-lg bg-primary/20 border border-primary/30 text-sm text-foreground mb-4">
                Network Design Problem
              </div>
              
              {/* Branches */}
              <div className="w-0.5 h-6 bg-primary/50" />
              
              <div className="flex gap-8 mb-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-0.5 h-6 bg-primary/50" />
                ))}
              </div>
              
              <div className="grid grid-cols-3 gap-4 w-full">
                {[
                  { name: '3-Tier', score: 75, selected: false },
                  { name: 'Spine-Leaf', score: 92, selected: true },
                  { name: 'Hybrid', score: 78, selected: false },
                ].map((approach, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg text-center ${
                      approach.selected 
                        ? 'bg-accent/20 border-2 border-accent' 
                        : 'bg-muted/30 border border-muted'
                    }`}
                  >
                    <p className="text-sm font-medium text-foreground">{approach.name}</p>
                    <p className={`text-lg font-mono mt-1 ${approach.selected ? 'text-accent' : 'text-muted-foreground'}`}>
                      {approach.score}%
                    </p>
                    {approach.selected ? (
                      <CheckCircle className="w-4 h-4 text-accent mx-auto mt-2" />
                    ) : (
                      <XCircle className="w-4 h-4 text-muted-foreground mx-auto mt-2" />
                    )}
                  </div>
                ))}
              </div>
              
              {/* Evaluation criteria */}
              <div className="mt-6 w-full text-xs text-muted-foreground">
                <p className="mb-2 font-medium text-foreground">Evaluation Criteria:</p>
                <div className="grid grid-cols-2 gap-2">
                  <span>• Scalability</span>
                  <span>• Cost</span>
                  <span>• Operational simplicity</span>
                  <span>• Failure resilience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Research insight */}
        <div className="mt-8 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
          <p className="text-sm text-muted-foreground">
            <strong className="text-secondary">Research Insight (2025):</strong> Tree-of-Thoughts and self-consistency improve accuracy on complex reasoning tasks by 15-30% but increase inference cost 3-5x.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default TreeOfThoughtsSlide;
